import { useEffect, useId, useRef, useState } from 'react';
import { Loader2, MapPin, Plane, Search, Star } from 'lucide-react';
import { usePlacesAutocomplete } from '../hooks/usePlacesAutocomplete';
import { formatPlace } from '../utils/placeFormat';

const MIN_SEARCH = 2;
const SEARCH_LIMIT = 25;

function PlaceOptionRow({ place, isActive, showPopularTag, onSelect }) {
  const isAirport = place.type === 'airport';
  const Icon = isAirport ? Plane : MapPin;
  const subtitle = isAirport
    ? [place.city_name, place.country_name].filter(Boolean).join(' · ')
    : place.country_name || '';

  return (
    <li className="city-selector__item">
      <button
        type="button"
        role="option"
        aria-selected={isActive}
        className={`city-selector__row${isActive ? ' is-active' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onSelect(place)}
      >
        <span className={`city-selector__icon-wrap${isAirport ? ' is-airport' : ' is-city'}`}>
          <Icon size={17} aria-hidden />
        </span>
        <span className="city-selector__row-body">
          <span className="city-selector__row-title">
            <strong>{place.name}</strong>
            {showPopularTag && (
              <span className="city-selector__tag">
                <Star size={10} aria-hidden /> Popular
              </span>
            )}
          </span>
          {subtitle ? <span className="city-selector__row-sub">{subtitle}</span> : null}
        </span>
        {place.code ? <span className="city-selector__code-badge">{place.code}</span> : null}
      </button>
    </li>
  );
}

/**
 * City / airport selector — popular list by default; local dummy search when typing.
 */
export default function CitySelector({
  value,
  onChange,
  placeholder = 'City or airport',
  required = false,
  popularPlaces = [],
  types = ['city', 'airport'],
  inputProps = {},
}) {
  const listId = useId();
  const rootRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);

  const trimmed = query.trim();
  const isSearching = trimmed.length >= MIN_SEARCH;

  const { results, loading, error } = usePlacesAutocomplete(isSearching ? trimmed : '', {
    types,
    minLength: MIN_SEARCH,
    limit: SEARCH_LIMIT,
  });

  const listItems = isSearching ? results : popularPlaces;

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    setActiveIndex(-1);
  }, [isSearching, trimmed]);

  useEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return undefined;
    }

    const updatePanelPosition = () => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPanelStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        left: rect.left,
        width: Math.max(rect.width, 280),
        zIndex: 500,
      });
    };

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open]);

  const selectPlace = (place) => {
    const label = formatPlace(place);
    setQuery(label);
    onChange(label, place);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true);
      return;
    }

    const total = listItems.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (total ? (i + 1) % total : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (total ? (i <= 0 ? total - 1 : i - 1) : 0));
    } else if (e.key === 'Enter' && activeIndex >= 0 && listItems[activeIndex]) {
      e.preventDefault();
      selectPlace(listItems[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="city-selector" ref={rootRef}>
      <input
        type="text"
        className="city-selector__input"
        value={query}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        {...inputProps}
      />

      {open && (
        <div
          className={`city-selector__panel${panelStyle ? ' city-selector__panel--fixed' : ''}`}
          id={listId}
          role="listbox"
          style={panelStyle || undefined}
        >
          <div className="city-selector__panel-head">
            <div className="city-selector__panel-title">
              {isSearching ? (
                <>
                  <Search size={14} aria-hidden />
                  <span>Search results</span>
                  {!loading && listItems.length > 0 && (
                    <span className="city-selector__count">{listItems.length}</span>
                  )}
                </>
              ) : (
                <>
                  <Star size={14} aria-hidden />
                  <span>Popular destinations</span>
                </>
              )}
            </div>
            {!isSearching && (
              <p className="city-selector__hint">Type at least 2 letters to search all cities &amp; airports</p>
            )}
          </div>

          {isSearching && loading && (
            <p className="city-selector__status">
              <Loader2 size={16} className="spin" aria-hidden />
              Searching worldwide…
            </p>
          )}

          {isSearching && error && !loading && (
            <p className="city-selector__status city-selector__status--error">
              Search unavailable. Check your connection and try again.
            </p>
          )}

          {isSearching && !loading && !error && listItems.length === 0 && (
            <p className="city-selector__status">No matches for &ldquo;{trimmed}&rdquo;. Try another name.</p>
          )}

          {!isSearching && popularPlaces.length === 0 && (
            <p className="city-selector__status">Type to search cities and airports.</p>
          )}

          {((!isSearching && popularPlaces.length > 0) ||
            (isSearching && !loading && !error && listItems.length > 0)) && (
            <ul className="city-selector__list">
              {listItems.map((place, i) => (
                <PlaceOptionRow
                  key={`${place.type}-${place.code}-${place.name}-${i}`}
                  place={place}
                  isActive={activeIndex === i}
                  showPopularTag={!isSearching}
                  onSelect={selectPlace}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
